import { useMemo } from 'react';
import { Layer, LayerProps, Source } from 'shared/map-exports';
import { Feature, Geometry } from 'geojson';
import { LatLng } from 'shared/types';
import { PlaceAddress, Place } from 'features/places/places.types';
import { useSelectedPlaceId } from 'features/places/hooks/useSelectedPlaceId';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { useMapLayersState } from 'state/useMapLayersState';
import {
  MapboxFeature,
  MapClickGetPois,
  MapClickHandler,
} from 'features/mapping/mapping.types';
import { PlacePoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import {
  FONT_NAMES,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
  PLACE_LABELS_SOURCE_ID,
} from 'features/mapping/styles/constants';
import { PlaceMapMarkerLabelColor } from 'features/mapping/mapping.constants';
import { getPlaceIcon } from 'features/mapping/icons';
import { history } from 'shared/history';

type PlaceLabelsLayerProps = {
  places: Place[];
};

type PlaceFeatureProperties = {
  id: number;
  name: string;
  icon: string;
  address: string;
};

const LABELS_MIN_ZOOM = 7;

const LABELS_LAYER_ID = 'place-labels';
export const PLACE_PIN_ICONS_LAYER_ID = 'place-pin-icons';
const PLACE_ICONS_LAYER_ID = 'place-icons';

const pinIconStyle: LayerProps = {
  type: 'symbol',
  layout: {
    'icon-image': 'PlaceLocationPin',
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
  },
  paint: {
    'icon-opacity': 1,
  },
};

const placeIconStyle: LayerProps = {
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
    'icon-offset': [0, -12],
  },
  paint: {
    'icon-opacity': 1,
    'icon-color': '#000000',
  },
};

const labelStyleCommon: LayerProps = {
  type: 'symbol' as const,
  minzoom: LABELS_MIN_ZOOM,
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': [
      'format' as const,
      ['get', 'name'],
      {
        'text-font': ['literal', FONT_NAMES.bold],
      },
    ],
    'text-anchor': 'top',
    'text-justify': 'auto',
  },
  paint: {
    'text-halo-width': 1.8,
    'text-translate': [0, -2],
  },
};

const labelStyleLight: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': PlaceMapMarkerLabelColor,
    'text-halo-color': 'rgba(255,255,255,0.8)',
  },
};

const labelStyleDark: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0,0,0,0.8)',
  },
};

const toGeojsonFeature = (
  place: Place,
): Feature<Geometry, PlaceFeatureProperties> => {
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Point' as const,
      coordinates: [
        place.address.coordinates.longitude,
        place.address.coordinates.latitude,
      ],
    },
    properties: {
      id: place.id,
      name: place.name,
      icon: getPlaceIcon(place.icon).name,
      address: JSON.stringify(place.address),
    },
  };
};

export const placePoiClickHandler: MapClickGetPois<PlaceFeatureProperties> = (
  geoJsonFeatures,
) => {
  const place = geoJsonFeatures[0].properties;
  const address = JSON.parse(place.address) as PlaceAddress;
  const metadata: PlacePoi = {
    id: place.id,
    name: place.name,
    address,
  };

  return [
    {
      type: 'legacyPlace',
      metadata,
    },
  ];
};

export const resolvePlacePath = (
  geoJsonFeatures: MapboxFeature<PlaceFeatureProperties>[],
): string => {
  const place = geoJsonFeatures[0].properties;
  return `/place/${place.id}`;
};

export const placeClickHandler: MapClickHandler<PlaceFeatureProperties> = (
  geoJsonFeatures,
) => {
  const path = resolvePlacePath(geoJsonFeatures);

  if (!path) {
    return;
  }

  history.push(path);
};

export const PlacesLayer = (
  props: PlaceLabelsLayerProps,
): JSX.Element | null => {
  const { places } = props;
  const { isLightBaseLayer } = useMapLayersState();
  const labelStyle = isLightBaseLayer ? labelStyleLight : labelStyleDark;

  const selectedPlaceId = useSelectedPlaceId();

  const activePlaceLatLng: LatLng | null = useMemo(() => {
    if (selectedPlaceId === undefined) {
      return null;
    }

    const place = places.find((p) => p.id === selectedPlaceId);

    if (!place) {
      return null;
    }

    const { latitude, longitude } = place.address.coordinates;

    return {
      lat: latitude,
      lng: longitude,
    };
  }, [selectedPlaceId, places]);

  useCenterMap({
    latLng: activePlaceLatLng,
    drawerIsOpen: selectedPlaceId !== undefined,
    defaultSnapPointPct: 0.47,
    zoomIn: true,
  });

  const geojson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: places.map((place) => toGeojsonFeature(place)),
    }),
    [places],
  );

  return (
    <Source id={PLACE_LABELS_SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={PLACE_PIN_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...pinIconStyle}
      />
      <Layer
        id={PLACE_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...placeIconStyle}
      />
      <Layer
        id={LABELS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...labelStyle}
      />
    </Source>
  );
};
